import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, ChevronRight } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { FloatingToast } from "@/components/feedback/FloatingToast";
import { OperationalFilterSurface } from "@/components/operational/OperationalFilterSurface";
import {
  OperationalPageFrame,
  OperationalPageHeader,
  OperationalToolbar,
} from "@/components/operational/OperationalPageLayout";
import { getNumberedPageTokens } from "@/components/operational/OperationalPager";
import { OperationalSearchField } from "@/components/operational/OperationalSearchField";
import { OperationalSelect } from "@/components/operational/OperationalSelect";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { checkinCompletionPercentage } from "@/features/checkin/completion";
import { useCheckinPage } from "@/features/checkin/hooks";
import { formatCheckinUpdateTime } from "@/features/checkin/pageDisplay";
import type { CheckinStatus, EnrichedCheckin } from "@/features/checkin/types";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import "./CargasPage.css";

const PAGE_SIZE = 10;

const STATUS_LABELS: Record<CheckinStatus, string> = {
  CONCLUIDO: "Não iniciado",
  CARREGAMENTO: "Carregamento",
  ENCERRADO: "Encerrado",
};

const CHECKIN_STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "CONCLUIDO", label: "Não iniciado" },
  { value: "CARREGAMENTO", label: "Carregamento" },
  { value: "ENCERRADO", label: "Encerrado" },
];

type AppliedFilters = {
  status?: CheckinStatus;
  date?: string;
};

type QueryErrorToast = {
  title: string;
  message: string;
};

const numberFormatter = new Intl.NumberFormat("pt-BR", {
  maximumFractionDigits: 2,
});

const cargasDateFormatter = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function normalizeOperationalNumber(value: number) {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function formatOperationalNumber(value: number) {
  return numberFormatter.format(normalizeOperationalNumber(value));
}

type ItemMetrics = {
  checked: number;
  total: number;
  checkedText: string;
  totalText: string;
  progressLabel: string;
  isDeterminate: boolean;
  percentage: number;
};

function deriveItemMetrics(
  checkedValue: number,
  totalValue: number,
): ItemMetrics {
  const checked = normalizeOperationalNumber(checkedValue);
  const total = normalizeOperationalNumber(totalValue);
  const checkedText = formatOperationalNumber(checked);
  const totalText = formatOperationalNumber(total);
  const isDeterminate = total > 0 && checked <= total;
  const percentage = checkinCompletionPercentage(checked, total);

  return {
    checked,
    total,
    checkedText,
    totalText,
    progressLabel: `${checkedText} de ${totalText} itens conferidos`,
    isDeterminate,
    percentage,
  };
}

function formatCargasDate(value: string | undefined) {
  if (!value || Number.isNaN(new Date(value).getTime())) return null;
  return cargasDateFormatter.format(new Date(value));
}

function CheckinStatusLabel({ status }: { status: CheckinStatus }) {
  return (
    <span
      className={`cargas-status ${status === "CARREGAMENTO" ? "warning" : "muted"}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

function CheckinProgress({ metrics }: { metrics: ItemMetrics }) {
  return (
    <div className="cargas-progress">
      <div className="progress-copy">
        <span>
          {metrics.checkedText} de {metrics.totalText}
        </span>
        <strong>{Math.round(metrics.percentage)}%</strong>
      </div>
      <div
        className="progress"
        role="progressbar"
        aria-label={metrics.progressLabel}
        aria-valuemin={metrics.isDeterminate ? 0 : undefined}
        aria-valuenow={metrics.isDeterminate ? metrics.checked : undefined}
        aria-valuemax={metrics.isDeterminate ? metrics.total : undefined}
      >
        <span
          className="cargas-progress-value"
          style={{ width: `${metrics.percentage}%` }}
        />
      </div>
    </div>
  );
}

function CheckinLedgerRow({
  checkin,
  singularLabel,
}: {
  checkin: EnrichedCheckin;
  singularLabel: string;
}) {
  const navigate = useNavigate();
  const createdDate = formatCargasDate(checkin.created_at);
  const reference = `#${checkin.id.slice(0, 8).toUpperCase()}`;
  const itemMetrics = deriveItemMetrics(
    checkin.checked_items,
    checkin.total_items,
  );

  const open = () =>
    navigate({ to: "/cargas/$checkinId", params: { checkinId: checkin.id } });

  return (
    <div className="cargas-ledger-row loads-grid" role="row">
      <div className="identity-cell" role="cell">
        <button
          type="button"
          className="row-open"
          aria-label={`Abrir ${singularLabel} de ${checkin.client_name}, ${checkin.store_name}`}
          onClick={open}
        />
        <span className="cell-title">{checkin.client_name}</span>
        <span className="cell-sub">
          {checkin.store_name} · {reference}
        </span>
      </div>

      <div role="cell">
        <span className="mobile-label">Data</span>
        <span className="cell-title mono">
          {createdDate ?? "Data indisponível"}
        </span>
        <span className="cell-sub">
          {formatCheckinUpdateTime(checkin.updated_at)}
        </span>
      </div>

      <div role="cell">
        <span className="mobile-label">Status</span>
        <CheckinStatusLabel status={checkin.status} />
      </div>

      <div className="progress-cell" role="cell">
        <span className="mobile-label">Itens conferidos</span>
        <CheckinProgress metrics={itemMetrics} />
      </div>

      <div className="cargas-actions-cell" role="cell">
        <span className="row-chevron" aria-hidden="true" onClick={open}>
          <ChevronRight size={17} />
        </span>
        <div className="mobile-open">
          <button
            type="button"
            aria-label={`Abrir ${singularLabel} ${reference}`}
            onClick={open}
          >
            <ArrowRight size={17} aria-hidden="true" />
            Abrir
          </button>
        </div>
      </div>
    </div>
  );
}

function LedgerHeader() {
  return (
    <div className="cargas-ledger-head loads-grid" role="row">
      <span role="columnheader">Cliente / loja</span>
      <span role="columnheader">Data</span>
      <span role="columnheader">Status</span>
      <span role="columnheader">Itens conferidos</span>
      <span role="columnheader" aria-label="Abrir" />
    </div>
  );
}

function LedgerSkeleton({ label }: { label: string }) {
  return (
    <div className="cargas-skeleton" role="status" aria-label={label}>
      <div className="cargas-ledger" aria-hidden="true">
        <LedgerHeader />
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className="cargas-skeleton-row">
            <span className="cargas-skeleton-line cargas-skeleton-line--identity" />
            <span className="cargas-skeleton-line" />
            <span className="cargas-skeleton-line" />
            <span className="cargas-skeleton-line cargas-skeleton-line--progress" />
          </div>
        ))}
      </div>
    </div>
  );
}

function CargasFilterFields({
  status,
  date,
  onStatusChange,
  onDateChange,
}: {
  status: "" | CheckinStatus;
  date: string;
  onStatusChange: (status: "" | CheckinStatus) => void;
  onDateChange: (date: string) => void;
}) {
  return (
    <>
      <label className="cargas-filter-field">
        <span>Status</span>
        <OperationalSelect
          aria-label="Status"
          value={status}
          options={CHECKIN_STATUS_OPTIONS}
          menuLayer="overlay"
          onChange={(nextStatus) =>
            onStatusChange(nextStatus as "" | CheckinStatus)
          }
        />
      </label>

      <label className="cargas-filter-field">
        <span>Data</span>
        <input
          aria-label="Data"
          type="date"
          value={date}
          onInput={(event) => onDateChange(event.currentTarget.value)}
        />
      </label>
    </>
  );
}

export function CargasPage() {
  const { data: currentUser } = useCurrentUser();
  const isSupplier = currentUser?.role === "supplier";
  const singularLabel = isSupplier ? "carga" : "conferência";
  const pluralLabel = isSupplier ? "cargas" : "conferências";

  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 250);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});
  const [draftStatus, setDraftStatus] = useState<"" | CheckinStatus>("");
  const [draftDate, setDraftDate] = useState("");
  const [queryErrorToast, setQueryErrorToast] =
    useState<QueryErrorToast | null>(null);
  const lastQueryErrorEvent = useRef<{
    error: unknown;
    queryIdentity: string;
  } | null>(null);

  const queryIdentity = `${debouncedSearch}\u0000${appliedFilters.status ?? ""}\u0000${appliedFilters.date ?? ""}`;
  const previousQueryIdentity = useRef(queryIdentity);
  const effectiveOffset =
    previousQueryIdentity.current === queryIdentity ? offset : 0;

  useEffect(() => {
    if (previousQueryIdentity.current === queryIdentity) return;
    previousQueryIdentity.current = queryIdentity;
    setOffset(0);
  }, [queryIdentity]);

  const query = useMemo(
    () => ({
      limit: PAGE_SIZE,
      offset: effectiveOffset,
      search: debouncedSearch,
      ...(appliedFilters.status ? { status: appliedFilters.status } : {}),
      ...(appliedFilters.date ? { date: appliedFilters.date } : {}),
    }),
    [
      appliedFilters.date,
      appliedFilters.status,
      debouncedSearch,
      effectiveOffset,
    ],
  );
  const {
    data,
    isLoading,
    isFetching,
    isPlaceholderData,
    isError,
    error,
    refetch,
  } = useCheckinPage(query);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(effectiveOffset / PAGE_SIZE);
  const activeCount =
    Number(Boolean(appliedFilters.status)) +
    Number(Boolean(appliedFilters.date));
  const isInitialLoading = isLoading && items.length === 0;
  const isUpdating = isFetching && items.length > 0;
  const hasActiveQuery = Boolean(
    debouncedSearch || appliedFilters.status || appliedFilters.date,
  );

  useEffect(() => {
    if (!data || isPlaceholderData) return;

    const lastValidOffset =
      data.total > 0 ? Math.floor((data.total - 1) / PAGE_SIZE) * PAGE_SIZE : 0;
    if (effectiveOffset > lastValidOffset) setOffset(lastValidOffset);
  }, [data, effectiveOffset, isPlaceholderData]);

  useEffect(() => {
    if (!isError) {
      lastQueryErrorEvent.current = null;
      setQueryErrorToast(null);
      return;
    }

    const errorQueryIdentity = `${queryIdentity}\u0000${effectiveOffset}`;
    const lastEvent = lastQueryErrorEvent.current;
    if (
      lastEvent?.error === error &&
      lastEvent.queryIdentity === errorQueryIdentity
    ) {
      return;
    }

    lastQueryErrorEvent.current = {
      error,
      queryIdentity: errorQueryIdentity,
    };
    setQueryErrorToast({
      title: `Não foi possível carregar ${isSupplier ? "as cargas" : "as conferências"}`,
      message: getUserFacingErrorMessage(
        error,
        `Não foi possível carregar ${isSupplier ? "as cargas" : "as conferências"}. Tente novamente.`,
      ),
    });
  }, [effectiveOffset, error, isError, isSupplier, queryIdentity]);

  function handleFilterOpenChange(open: boolean) {
    if (open) {
      setDraftStatus(appliedFilters.status ?? "");
      setDraftDate(appliedFilters.date ?? "");
    }
    setFiltersOpen(open);
  }

  function handleCancelFilters() {
    setDraftStatus(appliedFilters.status ?? "");
    setDraftDate(appliedFilters.date ?? "");
  }

  function handleApplyFilters() {
    setAppliedFilters({
      ...(draftStatus ? { status: draftStatus } : {}),
      ...(draftDate ? { date: draftDate } : {}),
    });
    setOffset(0);
    setFiltersOpen(false);
  }

  function handleClearFilters() {
    setDraftStatus("");
    setDraftDate("");
    setAppliedFilters({});
    setOffset(0);
    setFiltersOpen(false);
  }

  function handleQueryErrorRetry() {
    setQueryErrorToast(null);
    void refetch();
  }

  return (
    <OperationalPageFrame
      size="standard"
      className="cargas-page app-page-frame"
    >
      <OperationalPageHeader title={isSupplier ? "Cargas" : "Conferências"} />

      <OperationalToolbar
        className="cargas-toolbar"
        search={
          <OperationalSearchField
            value={search}
            onChange={setSearch}
            placeholder={isSupplier ? "Buscar carga" : "Buscar cliente ou loja"}
            ariaLabel={isSupplier ? "Buscar cargas" : "Buscar conferências"}
          />
        }
        filters={
          <OperationalFilterSurface
            open={filtersOpen}
            activeCount={activeCount}
            onOpenChange={handleFilterOpenChange}
            onCancel={handleCancelFilters}
            onClear={handleClearFilters}
            onApply={handleApplyFilters}
          >
            <CargasFilterFields
              status={draftStatus}
              date={draftDate}
              onStatusChange={setDraftStatus}
              onDateChange={setDraftDate}
            />
          </OperationalFilterSurface>
        }
        status={
          <span
            className="cargas-update-status"
            role={isUpdating ? "status" : undefined}
            aria-label={isUpdating ? `Atualizando ${pluralLabel}` : undefined}
            aria-live="polite"
            data-visible={isUpdating ? "true" : undefined}
          >
            {isUpdating ? "Atualizando…" : ""}
          </span>
        }
      />

      {isInitialLoading ? (
        <LedgerSkeleton label={`Carregando ${pluralLabel}`} />
      ) : items.length > 0 ? (
        <section className="cargas-ledger">
          <div
            role="table"
            aria-label={isSupplier ? "Cargas" : "Conferências"}
            aria-busy={isUpdating}
          >
            <LedgerHeader />
            <div className="cargas-ledger-body" role="rowgroup">
              {items.map((checkin) => (
                <CheckinLedgerRow
                  key={checkin.id}
                  checkin={checkin}
                  singularLabel={singularLabel}
                />
              ))}
            </div>
          </div>
          {pageCount > 1 && (
            <footer className="cargas-pager">
              <span className="pager-range">
                {effectiveOffset + 1}–
                {Math.min(effectiveOffset + PAGE_SIZE, total)} de {total}
              </span>
              <nav
                className="pager-controls"
                aria-label={`Paginação de ${pluralLabel}`}
                aria-busy={isFetching}
              >
                <button
                  type="button"
                  disabled={isFetching || currentPage === 0}
                  onClick={() => setOffset((currentPage - 1) * PAGE_SIZE)}
                >
                  Anterior
                </button>
                {getNumberedPageTokens(currentPage, pageCount).map(
                  (token, index) =>
                    token === "ellipsis" ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="pager-ellipsis"
                        aria-hidden="true"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={token}
                        type="button"
                        className="pager-page"
                        data-mobile-far={
                          Math.abs(token - currentPage) > 1 ? "true" : undefined
                        }
                        aria-label={`Página ${token + 1}`}
                        aria-current={
                          token === currentPage ? "page" : undefined
                        }
                        disabled={isFetching}
                        onClick={() => setOffset(token * PAGE_SIZE)}
                      >
                        {token + 1}
                      </button>
                    ),
                )}
                <button
                  type="button"
                  disabled={isFetching || currentPage >= pageCount - 1}
                  onClick={() => setOffset((currentPage + 1) * PAGE_SIZE)}
                >
                  Próximo
                </button>
              </nav>
            </footer>
          )}
        </section>
      ) : !isError ? (
        <p className="cargas-empty">
          Nenhuma {singularLabel} {hasActiveQuery ? "encontrada" : "ainda"}
        </p>
      ) : null}

      {queryErrorToast && (
        <FloatingToast
          actionLabel="Tentar novamente"
          onAction={handleQueryErrorRetry}
          onDismiss={() => setQueryErrorToast(null)}
          tone="error"
          title={queryErrorToast.title}
          message={queryErrorToast.message}
        />
      )}
    </OperationalPageFrame>
  );
}
